import React, { FC, useEffect, useMemo } from 'react';

import {
  fetchWithTokenAndRegion,
  getAdminApiUrl,
} from '@adsk/acs-sc-web-platform-api';
import { useGetAllPaginatedData } from '@adsk/alloy-helpers';
import { NotificationsProvider } from '@adsk/alloy-react-notification';
import { TOTAL_REMOTE_LOADING_TIME } from '@adsk/exo-skeleton-api';

import { AccessLevelContextProvider } from './AccessLevelContext';
import { ProductEntryFlowContextProvider } from './ProductEntryFlowContext';
import { DefaultPaginatedResponse } from '../../clients/admin';
import { initi18n } from '../../helpers/i18n';
import usePerformanceTrackingWithEffects from '../../hooks/usePerformanceTrackingWithEffects';
import { HQAccount } from '../../models';
import { mergeResponsesData } from '../../utils';
import { ProjectListErrorBoundery } from '../ProjectListErrorBoundery';
import { ScrollContainer } from '../ScrollContainer';

const PROJECTS_PROVIDERS_TRACKING_ID = 'projects-providers';

const Providers: FC = ({ children }) => {
  const {
    loading: accountsLoading,
    error: accountsError,
    data: accounts,
  } = useGetAllPaginatedData({
    url: `${getAdminApiUrl()}/v1/accounts`,
    fetcher: fetchWithTokenAndRegion,
  });

  usePerformanceTrackingWithEffects({
    trackingId: PROJECTS_PROVIDERS_TRACKING_ID,
    loading: accountsLoading,
    parentTrackingId: TOTAL_REMOTE_LOADING_TIME,
  });

  const mergedAccounts = useMemo(
    () =>
      accounts.length > 0
        ? mergeResponsesData(accounts as DefaultPaginatedResponse<HQAccount>[])
        : [],
    [accounts],
  );

  useEffect(() => {
    const init = async () => {
      await initi18n();
    };
    void init();
  }, []);

  const notAuthorized = accountsError?.status === 401;

  return (
    <NotificationsProvider>
      <ScrollContainer>
        <AccessLevelContextProvider
          accountsError={accountsError}
          accountsLoading={accountsLoading || notAuthorized}
          accounts={mergedAccounts}
        >
          <ProductEntryFlowContextProvider>
            {children}
          </ProductEntryFlowContextProvider>
        </AccessLevelContextProvider>
      </ScrollContainer>
    </NotificationsProvider>
  );
};

const ProvidersWithErrorBoundery: FC = ({ children }) => (
  <ProjectListErrorBoundery>
    <Providers>{children}</Providers>
  </ProjectListErrorBoundery>
);

export default ProvidersWithErrorBoundery;
